import type { ExtensionInstance, PaginatedApiResult } from '@mangovoice/types'

import type {
  CreateExtensionPayload,
  CreateSharedVoicemailPayload,
  MassEditLinesPayload,
  MobileDevice,
  UpdateCallQueuePayload,
  UpdateConferenceRoomPayload,
  UpdateExtensionPayload,
  UpdateRingGroupPayload,
  UserGroupOption,
  VoipProfile,
} from '../types/api.types'

interface ApiResponse<T> {
  data: T | null
  ok: boolean
}

/**
 * Reset-sip-secret response. On failure the API returns a `message` describing
 * why (the action is superuser-only, so permission_denied is common).
 */
export interface ResetSipSecretResult {
  message?: string
  new_value?: string
  affected_devices?: { id: number; station_label: string | null }[]
}

/**
 * API interface that consumers must implement.
 * This allows the library to be API-agnostic — apps provide
 * their own transport (Axios, fetch, MangoApi wrapper, etc.).
 */
export interface ExtensionsApiClient {
  getExtensions: (params: {
    pbx_id?: number
    type?: string
    limit?: number
    offset?: number
    search?: string
    ordering?: string
    status_filter?: 'active' | 'inactive'
    assigned_user_id?: number[]
    location_id?: number[]
  }) => Promise<ApiResponse<PaginatedApiResult<ExtensionInstance>>>

  getExtensionInfo: (
    extensionId: number | string,
  ) => Promise<ApiResponse<ExtensionInstance>>

  createExtension: (
    pbxId: number,
    payload: CreateExtensionPayload,
  ) => Promise<ApiResponse<ExtensionInstance>>

  createSharedVoicemail: (
    pbxId: number,
    payload: CreateSharedVoicemailPayload,
  ) => Promise<ApiResponse<ExtensionInstance>>

  deleteExtension: (extensionId: number | string) => Promise<ApiResponse<void>>

  deleteAndReassignLine: (
    pbxId: number | string,
    extensionId: number | string,
    reassignToExtensionId: number | null,
  ) => Promise<ApiResponse<void>>

  updateExtension: (
    extensionId: number | string,
    payload: UpdateExtensionPayload,
  ) => Promise<ApiResponse<ExtensionInstance>>

  updateRingGroup: (
    pbxId: number,
    extensionId: number | string,
    payload: UpdateRingGroupPayload & {
      name?: string
      number?: string
      omit_from_directory?: boolean
    },
  ) => Promise<ApiResponse<ExtensionInstance>>

  updateCallQueue: (
    pbxId: number,
    extensionId: number | string,
    payload: UpdateCallQueuePayload,
  ) => Promise<ApiResponse<ExtensionInstance>>

  updateConferenceRoom: (
    pbxId: number,
    extensionId: number | string,
    payload: UpdateConferenceRoomPayload & {
      name?: string
      number?: string
      omit_from_directory?: boolean
    },
  ) => Promise<ApiResponse<ExtensionInstance>>

  updateExtensionUserGroups: (
    extensionId: number | string,
    payload: { user_group_ids: number[] },
  ) => Promise<ApiResponse<ExtensionInstance>>

  resendSecureFaxLink: (
    extensionId: number | string,
  ) => Promise<ApiResponse<void>>

  updateLinesLocation: (params: {
    pbx_id: number
    extension_ids: number[]
    location_id: number
  }) => Promise<ApiResponse<{ updated: number }>>

  massEditLines: (
    params: MassEditLinesPayload & { pbx_id: number },
  ) => Promise<ApiResponse<{ updated: number }>>

  resetSipSecret: (
    pbxId: number | string,
    extensionId: number | string,
  ) => Promise<ApiResponse<ResetSipSecretResult>>

  clearDialNow: (extensionId: number | string) => Promise<ApiResponse<void>>

  getMobileDevices: (
    extensionId: number | string,
  ) => Promise<ApiResponse<MobileDevice[]>>

  setLineActiveState: (
    pbxId: number | string,
    extensionId: number | string,
    active: boolean,
  ) => Promise<ApiResponse<ExtensionInstance>>

  getPbxUserGroups: (params: {
    pbx_id: number
    search?: string
    limit?: number
  }) => Promise<ApiResponse<{ results: UserGroupOption[] }>>

  getLocations: (params: {
    pbx_id: number
    search?: string
  }) => Promise<ApiResponse<{ results: { id: number; name: string }[] }>>

  getSystemDefaults: (params: { pbx_id: number }) => Promise<
    ApiResponse<{
      default_area_code?: string
      default_caller_id?: string
      default_time_zone?: string
    }>
  >

  getPlaylists: (params: { pbx_id: number }) => Promise<
    ApiResponse<
      {
        id: number
        name: string
        is_default?: boolean
        type?: 'list' | 'stream'
        track_count?: number
        url?: string | null
      }[]
    >
  >

  getPhoneNumbers: (params: {
    pbx_id: number
  }) => Promise<ApiResponse<{ results?: { name?: string; number?: string }[] }>>

  getSoundClips: (params: {
    pbx_id: number
    limit?: number
  }) => Promise<ApiResponse<{ results?: { id: number; name: string }[] }>>

  /** Fetch users for a PBX. Returns array of {id, full_name}. */
  getUsers: (params: {
    pbx_id: number
  }) => Promise<ApiResponse<{ id: number; full_name?: string }[]>>

  /** Fetch VoIP profiles for a PBX, filtered by subject_type. */
  getVoipProfiles: (params: {
    pbx_id: number
    subject_type?: string
  }) => Promise<ApiResponse<VoipProfile[]>>
}

export type ExtensionsApi = ExtensionsApiClient

/**
 * @deprecated No-op pass-through kept for backward compatibility.
 * Consumers should use `ExtensionsApiClient` / `ExtensionsApi` directly.
 * Will be removed once apps are migrated to `@mangovoice/mango-api-client`.
 */
export const createExtensionsApi = (
  client: ExtensionsApiClient,
): ExtensionsApi => client
